import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { tts } from '../utils/tts';
import './CharacterOrigin.css';

const CharacterOrigin = ({ character, onClose }) => {
  const [description, setDescription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);

  useEffect(() => {
    const fetchOrigin = async () => {
      if (!character || !character.hanzi) return;

      setLoading(true);
      setError(null);

      try {
        // 1. Check Firestore cache
        const cacheRef = doc(db, 'characterOrigins', character.hanzi);
        const cacheSnap = await getDoc(cacheRef);

        if (cacheSnap.exists()) {
          setDescription(cacheSnap.data().description);
          setLoading(false);
          return;
        }

        // 2. Fetch from backend if not cached
        const token = await auth.currentUser.getIdToken();
        const response = await fetch('https://backend.hanzi-flashcards.com/api/character-origin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ character: character.hanzi })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to fetch character origin');
        }

        const data = await response.json();
        const originDescription = data.description;

        setDescription(originDescription);

        // 3. Save to cache
        await setDoc(cacheRef, {
          character: character.hanzi,
          description: originDescription,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

      } catch (err) {
        console.error('Error fetching character origin:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrigin();
  }, [character]);

  return (
    <div className="character-origin-overlay" onClick={onClose}>
      <div className="character-origin-modal" onClick={e => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>&times;</button>

        <div className="character-origin-content">
          <div className="character-header">
            <h1
              className={`hanzi-large ${isSpeaking ? 'speaking' : ''}`}
              onClick={() => {
                tts.unlock();
                tts.speak(
                  character.hanzi,
                  () => setIsSpeaking(true),
                  () => setIsSpeaking(false)
                );
              }}
            >
              {character.hanzi}
            </h1>
            <div className="character-meta">
              <div className="pinyin">{character.pinyin}</div>
              <div className="translation">{character.translation}</div>
            </div>
          </div>

          <div className="origin-details">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading character origin...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <span className="error-icon">⚠️</span>
                <p>{error}</p>
              </div>
            ) : description ? (
              <div className="description-container">
                <h2>Character Origin</h2>
                <div className="markdown-content">
                  <ReactMarkdown>
                    {description}
                  </ReactMarkdown>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterOrigin;
